/**
 * API / Pusher payloads sometimes nest the real payload under `data`, then again under `data.data`
 * (same pattern as `getLicensePlateFromNotification`).
 */
function walkNotificationDataRoots(
  data: Record<string, any> | undefined | null
): Record<string, any>[] {
  if (!data) {
    return [];
  }
  const roots: Record<string, any>[] = [data];
  const d1 = data['data'];
  if (d1 && typeof d1 === 'object' && !Array.isArray(d1)) {
    const layer1 = d1 as Record<string, any>;
    roots.push(layer1);
    const d2 = layer1['data'];
    if (d2 && typeof d2 === 'object' && !Array.isArray(d2)) {
      roots.push(d2 as Record<string, any>);
    }
  }
  return roots;
}

function recordHasLprCaptureSignals(root: Record<string, any>): boolean {
  const nested = root['lpr_entry'] as Record<string, any> | undefined;

  const sourceSignals = [
    root['order_source'],
    root['orderSource'],
    root['source_context'],
    root['sourceContext'],
    root['scan_source'],
    root['scanSource'],
  ];
  if (
    sourceSignals.some(value => typeof value === 'string' && value.toLowerCase().includes('lpr'))
  ) {
    return true;
  }
  if (root['is_lpr_upload'] === true || root['isLprUpload'] === true) {
    return true;
  }

  const lprIdKeys = ['lpr_entry_id', 'lprEntryId'] as const;
  for (const k of lprIdKeys) {
    const top = root[k];
    if (typeof top === 'string' && top.trim().length > 0) {
      return true;
    }
    const inner = nested?.[k];
    if (typeof inner === 'string' && inner.trim().length > 0) {
      return true;
    }
  }
  const nestedEntryId = nested?.['id'];
  if (typeof nestedEntryId === 'string' && nestedEntryId.trim().length > 0) {
    return true;
  }

  return recordHasNonEmptyLprAxisImageUrl(root);
}

const LPR_PLATE_IMAGE_S3_URL_KEYS = ['plate_image_s3_url', 'plateImageS3Url'] as const;
const LPR_PLATE_IMAGE_S3_URL_ARRAY_KEYS = ['plate_image_s3_urls', 'plateImageS3Urls'] as const;
const LPR_VEHICLE_IMAGE_S3_URL_KEYS = ['image_s3_url', 'imageS3Url'] as const;

const LPR_AXIS_IMAGE_URL_KEYS = [
  'plate_image_s3_url',
  'plateImageS3Url',
  'image_s3_url',
  'imageS3Url',
  'lpr_image_url',
  'lprImageUrl',
  'axis_plate_image_url',
  'axis_image_url',
  'plate_image_url',
  'image_url',
  'imageUrl',
] as const;

const LPR_AXIS_IMAGE_URL_ARRAY_KEYS = [
  'plate_image_s3_urls',
  'plateImageS3Urls',
  'image_s3_urls',
  'imageS3Urls',
] as const;

function nonEmptyImageUrl(value: any): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function recordHasNonEmptyLprAxisImageUrl(root: Record<string, any>): boolean {
  const nested = root['lpr_entry'] as Record<string, any> | undefined;
  const roots = [root, nested].filter((r): r is Record<string, any> => !!r);

  for (const layer of roots) {
    for (const key of LPR_AXIS_IMAGE_URL_KEYS) {
      if (nonEmptyImageUrl(layer[key])) {
        return true;
      }
    }
    for (const key of LPR_AXIS_IMAGE_URL_ARRAY_KEYS) {
      const list = layer[key];
      if (!Array.isArray(list)) {
        continue;
      }
      for (const item of list) {
        if (nonEmptyImageUrl(item)) {
          return true;
        }
      }
    }
  }
  return false;
}

function resolveUrlFromRecordByKeys(
  root: Record<string, any>,
  stringKeys: readonly string[],
  arrayKeys: readonly string[] = []
): string | null {
  const nested = root['lpr_entry'] as Record<string, any> | undefined;
  const layers = [root, nested].filter((r): r is Record<string, any> => !!r);

  for (const layer of layers) {
    for (const key of stringKeys) {
      const url = nonEmptyImageUrl(layer[key]);
      if (url) {
        return url;
      }
    }
    for (const key of arrayKeys) {
      const list = layer[key];
      if (!Array.isArray(list)) {
        continue;
      }
      for (let i = list.length - 1; i >= 0; i--) {
        const url = nonEmptyImageUrl(list[i]);
        if (url) {
          return url;
        }
      }
    }
  }
  return null;
}

function resolveLprAxisImageUrlFromRecord(root: Record<string, any>): string | null {
  return resolveUrlFromRecordByKeys(root, LPR_AXIS_IMAGE_URL_KEYS, LPR_AXIS_IMAGE_URL_ARRAY_KEYS);
}

function resolveUrlFromNotificationDataByKeys(
  data: Record<string, any> | undefined | null,
  stringKeys: readonly string[],
  arrayKeys: readonly string[] = []
): string | null {
  for (const root of walkNotificationDataRoots(data)) {
    const url = resolveUrlFromRecordByKeys(root, stringKeys, arrayKeys);
    if (url) {
      return url;
    }
  }
  return null;
}

/** Plate crop URL only (`plate_image_s3_url`). */
export function resolvePlateImageS3UrlFromNotificationData(
  data: Record<string, any> | undefined | null
): string | null {
  return resolveUrlFromNotificationDataByKeys(
    data,
    LPR_PLATE_IMAGE_S3_URL_KEYS,
    LPR_PLATE_IMAGE_S3_URL_ARRAY_KEYS
  );
}

/** Full lane / vehicle capture URL only (`image_s3_url`). */
export function resolveVehicleImageS3UrlFromNotificationData(
  data: Record<string, any> | undefined | null
): string | null {
  return resolveUrlFromNotificationDataByKeys(data, LPR_VEHICLE_IMAGE_S3_URL_KEYS);
}

/** First lane / plate image URL on the payload (walks nested `data` layers). */
export function resolveLprAxisImageUrlFromNotificationData(
  data: Record<string, any> | undefined | null
): string | null {
  for (const root of walkNotificationDataRoots(data)) {
    const url = resolveLprAxisImageUrlFromRecord(root);
    if (url) {
      return url;
    }
  }
  return null;
}

/** True when payload includes a loadable lane / plate image URL (not merely `image_source`). */
export function notificationDataHasLprAxisImageUrl(
  data: Record<string, any> | undefined | null
): boolean {
  return (
    !!resolvePlateImageS3UrlFromNotificationData(data) ||
    !!resolveVehicleImageS3UrlFromNotificationData(data) ||
    !!resolveLprAxisImageUrlFromNotificationData(data)
  );
}

function recordHasLprEntryId(root: Record<string, any>): boolean {
  const nested = root['lpr_entry'] as Record<string, any> | undefined;
  const lprIdKeys = ['lpr_entry_id', 'lprEntryId'] as const;
  for (const k of lprIdKeys) {
    const top = root[k];
    if (typeof top === 'string' && top.trim().length > 0) {
      return true;
    }
    const inner = nested?.[k];
    if (typeof inner === 'string' && inner.trim().length > 0) {
      return true;
    }
  }
  const nestedEntryId = nested?.['id'];
  return typeof nestedEntryId === 'string' && nestedEntryId.trim().length > 0;
}

function recordIndicatesLprFailureWithoutImage(root: Record<string, any>): boolean {
  const failureType = root['failure_type'] ?? root['failureType'];
  if (typeof failureType === 'string' && failureType.trim().length > 0) {
    return true;
  }
  const failureReason = root['failure_reason'] ?? root['failureReason'];
  if (typeof failureReason === 'string' && failureReason.trim().length > 0) {
    return true;
  }
  const status = root['status'];
  if (status === 400 || status === '400') {
    return true;
  }
  return false;
}

/**
 * True when a lane / webflow image may still arrive on `lpr-axis-images-ready` (no URL in payload yet).
 * False when the Pusher payload is already complete with no image URLs (e.g. invalid-plate failure).
 */
export function notificationDataIndicatesLprAxisImageAwaitingPusher(
  data: Record<string, any> | undefined | null
): boolean {
  if (notificationDataHasLprAxisImageUrl(data)) {
    return false;
  }
  for (const root of walkNotificationDataRoots(data)) {
    if (!recordHasLprEntryId(root)) {
      continue;
    }
    // UI image scan (`get-lp-image`), Axis/Rhombus and Vaxtor all emit a follow-up images-ready event.
    if (root['is_lpr_upload'] === true || root['isLprUpload'] === true) {
      return true;
    }
    const imageSource = root['image_source'] ?? root['imageSource'];
    if (typeof imageSource === 'string') {
      const normalized = imageSource.trim().toLowerCase();
      if (normalized === 'axis' || normalized === 'rhombus' || normalized === 'webflow') {
        return true;
      }
      // vaxtor (CWS-1641): the server says whether images will follow (inline
      // images uploaded in the background, or the GateController relay uploading
      // them after the event). A scan that carried none must not wait.
      if (normalized === 'vaxtor') {
        return root['images_pending'] === true || root['imagesPending'] === true;
      }
    }
    if (recordIndicatesLprFailureWithoutImage(root)) {
      return false;
    }
  }
  return false;
}

/** Whether the order-request layout should show the lane preview column (with or without a URL). */
export function notificationDataShowsLprPlatePreviewColumn(
  data: Record<string, any> | undefined | null,
  notificationType?: string | null
): boolean {
  if (notificationType === 'invalid-license-plate') {
    return true;
  }
  return notificationDataHasLprCaptureForOrderSource(data);
}

/**
 * True when the open notification modal row is an LPR lane / camera capture flow (not manual SaaS "+").
 * Requires an LPR entry id plus lane signals or `invalid-license-plate`.
 */
export function isNotificationModalLprEntryFlow(
  notificationType: string | undefined | null,
  data: Record<string, any> | undefined | null
): boolean {
  const entryId = String(
    data?.['lpr_entry_id'] ??
      data?.['lprEntryId'] ??
      data?.['id'] ??
      data?.['licence_plate_id'] ??
      data?.['license_plate_id'] ??
      ''
  ).trim();
  if (!entryId) {
    return false;
  }
  if (notificationType === 'invalid-license-plate') {
    return true;
  }
  return notificationDataHasLprCaptureForOrderSource(data);
}

/**
 * Detects LPR / lane-camera / header-scan (`get-lp-image`) context on notification payload.
 * Manual sales or header "+" flows use `create-order-request` without these fields → `order_source: saas`;
 * once a plate image or LPR entry is present (e.g. scan after "+"), orders should use `order_source: lpr`.
 */
export function notificationDataHasLprCaptureForOrderSource(
  data: Record<string, any> | undefined | null
): boolean {
  for (const root of walkNotificationDataRoots(data)) {
    if (recordHasLprCaptureSignals(root)) {
      return true;
    }
  }
  return false;
}

/** Matches `TapToPayOrderSource` in tap-to-pay-order-params.util (kept here to avoid circular imports). */
export type TapToPayOrderSource = 'lpr' | 'saas';

/**
 * Stripe Terminal metadata `source` must match POST /order `order_source`.
 * Header "+" / manual checkout → `saas`; LPR lane capture → `lpr`.
 */
export function resolveTapToPayOrderSource(
  apiOrder?: Record<string, any> | null,
  notificationData?: Record<string, any> | null
): TapToPayOrderSource {
  const nestedOrder = notificationData?.['order'];
  const orderFromNotification =
    nestedOrder && typeof nestedOrder === 'object' && !Array.isArray(nestedOrder)
      ? (nestedOrder as Record<string, any>)
      : null;

  const raw =
    apiOrder?.['order_source'] ??
    apiOrder?.['orderSource'] ??
    orderFromNotification?.['order_source'] ??
    orderFromNotification?.['orderSource'] ??
    notificationData?.['order_source'] ??
    notificationData?.['orderSource'];

  if (raw === 'saas' || raw === 'lpr') {
    return raw;
  }

  return notificationDataHasLprCaptureForOrderSource(notificationData) ? 'lpr' : 'saas';
}
